import type React from "react";
import { useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { styled } from "styled-components";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { userIsConnectedTo } from "simplydo/core/connections";
import { Button, Icon, Menu, Progress } from "simplydo/react/ui";
import { Popup } from "simplydo/react/ui";

import { useAppSelector } from "store";

import { DropZone, type DropZoneConfigProps } from "./DropZone";
import {
  FileUploadWrapper,
  type FileUploadWrapperProps,
  type FileUploadWrapperRef,
} from "./FileUploadWrapper";
import type { DragFile, UploadState } from "./uploadUtils";

const ChooserPopup = styled(Popup)`
  max-width: 400px !important;
  .menu {
    max-width: 400px !important;
    min-width: 300px !important;
  }
`;

export type FileUploaderProps = {
  popupProps?: any;
  dropzoneProps?: DropZoneConfigProps;
  style?: React.CSSProperties;
} & Omit<
  FileUploadWrapperProps,
  "children" | "setUploadState" | "setDraggedOverFiles"
>;

export const FileUploaderPopup = ({
  accept,
  forType,
  forId,
  onError,
  popupProps = {},
  dropzoneProps = {},
  onComplete,
  allowMultiple,
  onCompleteMultiple,
  maxMBFileSize = 100,
}: FileUploaderProps) => {
  const { t } = useTranslation();
  const uploadRef = useRef<FileUploadWrapperRef>(null);

  const [draggedOverFiles, setDraggedOverFiles] = useState<Array<DragFile>>([]);
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    uploadProgress: 0,
  });

  const user = useAppSelector((state) => state.user);
  const connected365 = userIsConnectedTo(user, "office365");

  const [open, setOpen] = useState(false);
  const openPopup = useCallback(() => setOpen(true), []);
  const chooseFile = useCallback(() => {
    uploadRef.current?.chooseFile();
    setOpen(false);
  }, []);
  const chooseOneDriveFile = useCallback(() => {
    uploadRef.current?.openOneDrive();
    setOpen(false);
  }, []);
  const handleChooseFileKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      chooseFile();
    }
  };
  const handleChooseOneDriveFileKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      chooseOneDriveFile();
    }
  };

  return (
    <FileUploadWrapper
      accept={accept}
      forType={forType}
      forId={forId}
      ref={uploadRef}
      setUploadState={setUploadState}
      setDraggedOverFiles={setDraggedOverFiles}
      maxMBFileSize={maxMBFileSize}
      onComplete={onComplete}
      allowMultiple={allowMultiple}
      onCompleteMultiple={onCompleteMultiple}
      onError={onError}
    >
      {connected365 ?
        <ChooserPopup
          trapFocus
          mountNode={document.getElementById("semantic-modal-mount-node")}
          style={{ zIndex: 1500 }}
          on="click"
          open={open}
          position="top center"
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          trigger={
            <div
              tabIndex={0}
              role="button"
              onKeyDown={handleClickForKeyboard(openPopup)}
            >
              <DropZone
                uploadState={uploadState}
                setUploadState={setUploadState}
                draggedOverFiles={draggedOverFiles}
                disabled={!!popupProps.disabled}
                allowMultiple={allowMultiple}
                accept={accept}
              />
            </div>
          }
          content={
            <div>
              <h5 style={{ marginTop: 0 }}>Upload a file</h5>
              <p style={{ color: "grey" }}>
                The maximum file size is {maxMBFileSize}MB.
              </p>
              <Menu vertical borderless>
                {uploadState.isUploading && (
                  <Progress
                    percent={uploadState.uploadProgress}
                    indicating
                    progress
                    style={{ margin: 5 }}
                  />
                )}
                <Menu.Item
                  tabIndex={0}
                  role="button"
                  onClick={chooseFile}
                  onKeyDown={handleChooseFileKeyDown}
                >
                  <Icon name="upload" />
                  {t("ideas.actions.chooseFile")}
                </Menu.Item>
                {userIsConnectedTo(user, "office365") ?
                  <Menu.Item
                    tabIndex={0}
                    role="button"
                    onClick={chooseOneDriveFile}
                    onKeyDown={handleChooseOneDriveFileKeyDown}
                    icon="microsoft"
                    content="Choose from OneDrive"
                  />
                : null}
              </Menu>

              <div style={{ textAlign: "left", marginTop: 20 }}>
                <Button
                  basic
                  size="tiny"
                  content={t("generic.cancel")}
                  onClick={() => {
                    setOpen(false);
                  }}
                />
              </div>
            </div>
          }
          {...popupProps}
        />
      : <DropZone
          uploadState={uploadState}
          setUploadState={setUploadState}
          draggedOverFiles={draggedOverFiles}
          disabled={!!popupProps.disabled}
          allowMultiple={allowMultiple}
          accept={accept}
          onClick={() => {
            uploadRef.current?.chooseFile();
          }}
        />
      }
    </FileUploadWrapper>
  );
};
